
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import type { Tool } from '@/data/tools';

interface PlatformData {
  platformName: string;
  platformSeoDescription: string;
  allTools: Tool[];
  filteredTools: Tool[];
  categories: { id: string, name: string }[];
  sports: { id: string, name: string }[];
  loading: boolean;
}

export const usePlatformData = (slug: string | undefined): [
  PlatformData,
  {
    setFilteredTools: React.Dispatch<React.SetStateAction<Tool[]>>;
  }
] => {
  const { toast } = useToast();
  const [platformName, setPlatformName] = useState<string>('');
  const [platformSeoDescription, setPlatformSeoDescription] = useState<string>('');
  const [allTools, setAllTools] = useState<Tool[]>([]);
  const [filteredTools, setFilteredTools] = useState<Tool[]>([]);
  const [categories, setCategories] = useState<{ id: string, name: string }[]>([]);
  const [sports, setSports] = useState<{ id: string, name: string }[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlatformData = async () => {
      if (!slug) return;
      
      setLoading(true);
      
      try {
        // First, get the platform details based on the URL slug
        const { data: platformData, error: platformError } = await supabase
          .from('platforms')
          .select('id, name, page_title, seo_description')
          .eq('url_slug', slug)
          .single();
        
        if (platformError) throw platformError;
        
        if (platformData) {
          setPlatformName(platformData.name);
          setPlatformSeoDescription(platformData.seo_description || '');
          
          // Then get all apps that are related to this platform
          const { data: appsData, error: appsError } = await supabase
            .from('apps')
            .select(`
              id,
              name,
              description,
              visit_url,
              url_slug,
              approved,
              featured,
              logo_url,
              app_platforms!inner(platform_id),
              app_categories(category_id, categories(name)),
              app_sports(sport_id, sports(name))
            `)
            .eq('approved', true)
            .eq('app_platforms.platform_id', platformData.id);
          
          if (appsError) throw appsError;
          
          if (appsData) {
            // Transform the data into the Tool format
            const transformedTools: Tool[] = appsData.map(app => {
              // Extract unique categories and sports
              const categories = Array.from(new Set(
                app.app_categories.map((c: any) => c.categories?.name).filter(Boolean)
              ));
              
              const sports = Array.from(new Set(
                app.app_sports.map((s: any) => s.sports?.name).filter(Boolean)
              ));
              
              return {
                id: app.id,
                name: app.name,
                logo: app.logo_url || "https://placehold.co/60x60",
                rating: 4.5, // Default rating
                reviews: 0, // Default reviews count
                bookmarks: 0, // Default bookmarks count
                pricing: "Free Trial", // Default pricing
                description: app.description,
                tags: [], // Could be derived from categories
                category: categories[0] || "",
                sports: sports,
                platforms: [platformData.name], // We know it has this platform
                featured: app.featured,
                visitUrl: app.visit_url,
                url_slug: app.url_slug,
              };
            });
            
            setAllTools(transformedTools);
            setFilteredTools(transformedTools);
          }
          
          // Fetch all categories
          const { data: categoriesData, error: categoriesError } = await supabase
            .from('categories')
            .select('id, name')
            .order('name');
            
          if (categoriesError) throw categoriesError;
          if (categoriesData) {
            setCategories(categoriesData);
          }
          
          // Fetch all sports
          const { data: sportsData, error: sportsError } = await supabase
            .from('sports')
            .select('id, name')
            .order('name');
            
          if (sportsError) throw sportsError;
          if (sportsData) {
            setSports(sportsData);
          }
        }
      } catch (error) {
        console.error('Error fetching platform data:', error);
        toast({
          title: "Error loading data",
          description: "There was a problem loading the platform data. Please try again.",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchPlatformData();
  }, [slug, toast]);

  return [
    {
      platformName,
      platformSeoDescription,
      allTools,
      filteredTools,
      categories,
      sports,
      loading
    },
    {
      setFilteredTools
    }
  ];
};
